if (!customElements.get("custom-announcement-bar")) {
  class CustomAnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.announcementPanel = null;
      this.campaignPanel = null;
      this.timerInterval = null;
      this.copyTimers = new WeakMap();
      this.announcementScrollTimers = new Set();
      this.announcementCloseButtons = new WeakSet();
      this.resizeFrame = null;
      this.lastViewportWidth = 0;
      this.handleResize = this.handleWindowResize.bind(this);
    }

    connectedCallback() {
      this.abortController?.abort();
      this.stopTimer();
      this.cleanupAnnouncementInlineScroll();
      this.abortController = new AbortController();
      this.lastViewportWidth = window.innerWidth;
      this.announcementPanel = this.querySelector("[data-announcement-panel]");
      this.campaignPanel = this.querySelector("[data-campaign-panel]");

      this.initializeAnnouncementPanel();
      this.initializeCampaign();
      this.initializeAnnouncementInlineScroll();
      this.updateAnnouncementHeight();
      this.observeStickyHeight();
      this.recalculateAfterFontsLoad();

      window.addEventListener("resize", this.handleResize, {
        signal: this.abortController.signal,
      });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      this.stopTimer();
      this.cleanupAnnouncementInlineScroll();
      this.heightResizeObserver?.disconnect();
      this.heightResizeObserver = null;

      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = null;
      }
    }

    observeStickyHeight() {
      this.heightResizeObserver?.disconnect();

      if (typeof ResizeObserver === "undefined") return;

      this.heightResizeObserver = new ResizeObserver(() => {
        this.updateAnnouncementHeight();
      });

      [this.campaignPanel, this.announcementPanel].forEach((element) => {
        if (element) this.heightResizeObserver.observe(element);
      });

      if (this.clientHeight > 0) this.heightResizeObserver.observe(this);
    }

    initializeCampaign() {
      if (!this.campaignPanel) return;

      if (this.isPanelDismissed(this.storageKey)) {
        this.hideCampaign(false);
        return;
      }

      this.bindCloseButton();
      this.bindCopyButtons();
      this.initializeTimer();
    }

    get storageKey() {
      return this.campaignPanel?.dataset.storageKey || "holzkern:campaign-bar:dismissed";
    }

    get announcementStorageKey() {
      return this.announcementPanel?.dataset.storageKey || "holzkern:announcement-bar:dismissed";
    }

    isPanelDismissed(storageKey) {
      try {
        return localStorage.getItem(storageKey) === "true";
      } catch (error) {
        return false;
      }
    }

    persistDismissal(storageKey) {
      try {
        localStorage.setItem(storageKey, "true");
      } catch (error) {
        // Ignore storage restrictions and still hide the bar for the current page view.
      }
    }

    initializeAnnouncementPanel() {
      if (!this.announcementPanel) return;

      if (this.isPanelDismissed(this.announcementStorageKey)) {
        this.hideAnnouncement(false);
        return;
      }

      this.bindAnnouncementCloseButtons();
    }

    bindCloseButton() {
      const closeButton = this.campaignPanel.querySelector("[data-campaign-close]");
      closeButton?.addEventListener("click", () => this.hideCampaign(true), {
        signal: this.abortController.signal,
      });
    }

    bindCopyButtons() {
      this.campaignPanel.querySelectorAll("[data-campaign-copy-code]").forEach((button) => {
        button.addEventListener("click", () => this.copyDiscountCode(button), {
          signal: this.abortController.signal,
        });
      });
    }

    copyDiscountCode(button) {
      const code = button.dataset.campaignCopyCode;
      if (!code) return;

      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(code).then(
          () => this.showCopiedState(button),
          () => this.copyDiscountCodeFallback(button, code),
        );
        return;
      }

      this.copyDiscountCodeFallback(button, code);
    }

    copyDiscountCodeFallback(button, code) {
      const input = document.createElement("textarea");
      input.value = code;
      input.setAttribute("readonly", "");
      input.style.position = "fixed";
      input.style.opacity = "0";
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      input.remove();
      this.showCopiedState(button);
    }

    showCopiedState(button) {
      const previousTimer = this.copyTimers.get(button);
      if (previousTimer) window.clearTimeout(previousTimer);

      button.classList.add("is-copied");

      const timer = window.setTimeout(() => {
        button.classList.remove("is-copied");
        this.copyTimers.delete(button);
      }, 1600);

      this.copyTimers.set(button, timer);
    }

    initializeTimer() {
      if (this.campaignPanel.dataset.showTimer !== "true" || !this.campaignPanel.dataset.endTime) return;

      const endTime = Date.parse(this.campaignPanel.dataset.endTime);
      const timerElements = this.timerElements;

      if (Number.isNaN(endTime) || !timerElements.hours || !timerElements.minutes || !timerElements.seconds) {
        this.campaignPanel.querySelector("[data-campaign-timer]")?.setAttribute("hidden", "");
        return;
      }

      this.endTime = endTime;
      this.timerInterval = window.setInterval(() => this.updateTimer(), 1000);
      this.updateTimer();
    }

    get timerElements() {
      return {
        hours: this.campaignPanel.querySelector("[data-campaign-hours]"),
        minutes: this.campaignPanel.querySelector("[data-campaign-minutes]"),
        seconds: this.campaignPanel.querySelector("[data-campaign-seconds]"),
      };
    }

    updateTimer() {
      const total = this.endTime - Date.now();
      const timerElements = this.timerElements;

      if (total <= 0) {
        timerElements.hours.textContent = "00";
        timerElements.minutes.textContent = "00";
        timerElements.seconds.textContent = "00";

        this.hideCampaign(false);
        this.stopTimer();
        return;
      }

      const totalSeconds = Math.floor(total / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      timerElements.hours.textContent = String(hours).padStart(2, "0");
      timerElements.minutes.textContent = String(minutes).padStart(2, "0");
      timerElements.seconds.textContent = String(seconds).padStart(2, "0");
    }

    hideCampaign(persist) {
      if (!this.campaignPanel) return;

      this.campaignPanel.hidden = true;
      this.stopTimer();

      if (persist) {
        this.persistDismissal(this.storageKey);
      }

      this.updateAnnouncementHeight();
    }

    bindAnnouncementCloseButtons() {
      this.announcementPanel?.querySelectorAll("[data-announcement-close]").forEach((button) => {
        if (this.announcementCloseButtons.has(button)) return;

        this.announcementCloseButtons.add(button);
        button.addEventListener("click", () => this.hideAnnouncement(true), {
          signal: this.abortController.signal,
        });
      });
    }

    hideAnnouncement(persist) {
      if (!this.announcementPanel) return;

      this.announcementPanel.hidden = true;
      this.cleanupAnnouncementInlineScroll();

      if (persist) {
        this.persistDismissal(this.announcementStorageKey);
      }

      this.updateAnnouncementHeight();
    }

    stopTimer() {
      if (!this.timerInterval) return;

      window.clearInterval(this.timerInterval);
      this.timerInterval = null;
    }

    handleWindowResize() {
      if (this.resizeFrame) window.cancelAnimationFrame(this.resizeFrame);

      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = null;

        const viewportWidth = window.innerWidth;
        const widthChanged = viewportWidth !== this.lastViewportWidth;
        this.lastViewportWidth = viewportWidth;

        if (widthChanged) this.initializeAnnouncementInlineScroll();

        this.updateAnnouncementHeight();
      });
    }

    recalculateAfterFontsLoad() {
      if (document.fonts?.status !== "loading") return;

      document.fonts.ready.then(() => {
        if (!this.isConnected) return;

        this.initializeAnnouncementInlineScroll();
        this.updateAnnouncementHeight();
      });
    }

    initializeAnnouncementInlineScroll() {
      this.cleanupAnnouncementInlineScroll();

      this.querySelectorAll("[data-announcement-inline]").forEach((bar) => {
        this.setupAnnouncementInlineScroll(bar);
      });
    }

    cleanupAnnouncementInlineScroll() {
      this.querySelectorAll("[data-announcement-inline]").forEach((bar) => {
        const track = bar.querySelector("[data-announcement-scroll-track]");

        bar.classList.remove("is-auto-scrolling", "is-auto-fading", "is-auto-sliding");
        bar.style.removeProperty("--announcement-scroll-distance");
        bar.style.removeProperty("--announcement-scroll-duration");

        track?.style.removeProperty("transform");
        bar.querySelectorAll(".announcement-bar__inline-item").forEach((item) => {
          item.classList.remove("is-active");
          item.removeAttribute("aria-hidden");
        });
      });

      this.announcementScrollTimers.forEach((timer) => window.clearInterval(timer));
      this.announcementScrollTimers.clear();
      this.querySelectorAll("[data-announcement-clone]").forEach((clone) => clone.remove());
    }

    setupAnnouncementInlineScroll(bar) {
      const viewport = bar.querySelector("[data-announcement-scroll-viewport]");
      const track = bar.querySelector("[data-announcement-scroll-track]");

      if (!viewport || !track) return;

      this.bindAnnouncementCloseButtons();

      const originalItems = Array.from(track.children).filter((item) => !item.hasAttribute("data-announcement-clone"));
      const announcementCount = Number(bar.dataset.announcementCount) || originalItems.length;
      const hasOverflow = track.scrollWidth > viewport.clientWidth + 1;
      const autoRotateEnabled = bar.dataset.announcementAutoRotate === "true";
      const scrollByCountEnabled = bar.dataset.announcementScrollByCount === "true";
      const animationType = bar.dataset.announcementAnimation || "slide";
      const shouldAutoScroll = this.shouldAutoScrollAnnouncement(announcementCount, hasOverflow, autoRotateEnabled, scrollByCountEnabled);
      const isMobileInline = window.matchMedia("(max-width: 989px)").matches;
      const canAutoRotate = autoRotateEnabled && originalItems.length > 1 && !this.prefersReducedMotion();

      if (isMobileInline && canAutoRotate) {
        if (animationType === "fade") {
          this.setupFadeAnnouncementScroll(bar, originalItems);
          return;
        }

        if (animationType === "loop") {
          this.setupLoopingAnnouncementScroll(bar, viewport, track, originalItems);
          return;
        }

        this.setupSlidingAnnouncementScroll(bar, track, originalItems);
        return;
      }

      if (!shouldAutoScroll) return;

      if (animationType === "fade") {
        this.setupFadeAnnouncementScroll(bar, originalItems);
        return;
      }

      this.setupLoopingAnnouncementScroll(bar, viewport, track, originalItems);
    }

    setupSlidingAnnouncementScroll(bar, track, items) {
      if (items.length <= 1) return;

      const interval = Number(bar.dataset.autoScrollInterval) || 5000;
      let activeIndex = 0;

      const setActiveState = () => {
        items.forEach((item, index) => {
          item.setAttribute("aria-hidden", index === activeIndex ? "false" : "true");
        });
      };

      const moveTrack = (index) => {
        track.style.transform = `translateX(${index * -100}%)`;
      };

      setActiveState();
      moveTrack(0);
      bar.classList.add("is-auto-sliding");

      const timer = window.setInterval(() => {
        if (this.shouldPauseAnnouncement(bar)) return;

        activeIndex = (activeIndex + 1) % items.length;
        setActiveState();
        moveTrack(activeIndex);
      }, interval);

      this.announcementScrollTimers.add(timer);
    }

    setupLoopingAnnouncementScroll(bar, viewport, track, items) {
      if (items.length <= 1) return;

      const appendCloneSet = () => {
        const fragment = document.createDocumentFragment();

        items.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.setAttribute("aria-hidden", "true");
          clone.setAttribute("data-announcement-clone", "");
          clone.querySelectorAll("a, button").forEach((element) => element.setAttribute("tabindex", "-1"));
          fragment.appendChild(clone);
        });

        track.appendChild(fragment);
      };

      appendCloneSet();

      const firstClone = track.querySelector("[data-announcement-clone]");
      const scrollDistance = firstClone
        ? firstClone.offsetLeft - items[0].offsetLeft
        : track.scrollWidth / 2;

      if (scrollDistance <= 0) return;

      const cloneSetsNeeded = Math.ceil(viewport.clientWidth / scrollDistance) + 1;
      for (let index = 1; index < cloneSetsNeeded; index += 1) appendCloneSet();

      const scrollDuration = Math.max(scrollDistance / 40, 8);

      bar.style.setProperty("--announcement-scroll-distance", `${scrollDistance}px`);
      bar.style.setProperty("--announcement-scroll-duration", `${scrollDuration}s`);
      bar.classList.add("is-auto-scrolling");
    }

    setupFadeAnnouncementScroll(bar, items) {
      if (items.length <= 1) return;

      let activeIndex = 0;
      const interval = Number(bar.dataset.autoScrollInterval) || 5000;

      items.forEach((item, index) => {
        const isActive = index === 0;
        item.classList.toggle("is-active", isActive);
        item.setAttribute("aria-hidden", isActive ? "false" : "true");
      });

      const timer = window.setInterval(() => {
        if (this.shouldPauseAnnouncement(bar)) return;

        items[activeIndex]?.classList.remove("is-active");
        items[activeIndex]?.setAttribute("aria-hidden", "true");

        activeIndex = (activeIndex + 1) % items.length;

        items[activeIndex]?.classList.add("is-active");
        items[activeIndex]?.setAttribute("aria-hidden", "false");
      }, interval);

      bar.classList.add("is-auto-fading");
      this.announcementScrollTimers.add(timer);
    }

    shouldAutoScrollAnnouncement(announcementCount, hasOverflow, autoRotateEnabled, scrollByCountEnabled) {
      if (this.prefersReducedMotion() || !autoRotateEnabled) return false;

      return hasOverflow || (scrollByCountEnabled && announcementCount >= 5);
    }

    prefersReducedMotion() {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    shouldPauseAnnouncement(bar) {
      const canHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
      return (canHover && bar.matches(":hover")) || bar.contains(document.activeElement);
    }

    updateAnnouncementHeight() {
      const stickyType = this.dataset.stickyType || "none";
      const visiblePanels = [this.campaignPanel, this.announcementPanel].filter(
        (panel) => panel && !panel.hidden,
      );
      const panelsHeight = visiblePanels.reduce((total, panel) => total + panel.offsetHeight, 0);
      const section = this.closest(".shopify-section");
      const sectionHeight = section?.clientHeight || 0;
      const hostHeight = this.clientHeight || 0;
      const announcementBarHeight = Math.max(sectionHeight, hostHeight, panelsHeight);

      document.documentElement.style.setProperty("--announcement-bar-height", `${announcementBarHeight}px`);

      let stickyAnnouncementHeight = 0;

      if (stickyType === "announcement" && this.announcementPanel && !this.announcementPanel.hidden) {
        stickyAnnouncementHeight = this.announcementPanel.offsetHeight;
      }

      document.documentElement.style.setProperty(
        "--custom-sticky-announcement-height",
        `${stickyAnnouncementHeight}px`,
      );

      if (typeof window.updateStickyAnnouncementStack === "function") {
        window.updateStickyAnnouncementStack();
      } else {
        document.documentElement.style.setProperty(
          "--sticky-announcement-height",
          `${stickyAnnouncementHeight}px`,
        );
      }
    }
  }

  customElements.define("custom-announcement-bar", CustomAnnouncementBar);
}
